import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";

/**
 * Games Page Mobile Navigation Component
 *
 * Secondary mobile navbar that appears above the main mobile navigation
 * Shows only on games category pages and navigates between categories
 * No icons - text navigation for game categories
 */
const GamesPageMobileNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [bottomOffset, setBottomOffset] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // Only show on games category pages
  const shouldShow = useMemo(() => {
    const p = location.pathname;
    return (
      p.startsWith("/games/slots") ||
      p.startsWith("/games/table") ||
      p.startsWith("/games/live") ||
      p.startsWith("/games/instant") ||
      p.startsWith("/games/favorites")
    );
  }, [location.pathname]);

  // Determine active tab (must be computed unconditionally to keep hooks order stable)
  const activeId = useMemo(() => {
    const p = location.pathname;
    if (p.startsWith("/games/slots")) return "slots";
    if (p.startsWith("/games/table")) return "table";
    if (p.startsWith("/games/live")) return "live";
    if (p.startsWith("/games/instant")) return "instant";
    if (p.startsWith("/games/favorites")) return "favorites";
    return null;
  }, [location.pathname]);

  // Calculate the height of the main mobile nav dynamically
  useEffect(() => {
    if (!shouldShow) {
      setIsVisible(false);
      return;
    }

    const calculateOffset = () => {
      const mainMobileNav = document.querySelector('nav[class*="bottom-0"]');
      if (mainMobileNav) {
        const height = mainMobileNav.offsetHeight;
        setBottomOffset(height);
      }
    };

    calculateOffset();
    window.addEventListener("resize", calculateOffset);

    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    return () => {
      window.removeEventListener("resize", calculateOffset);
      clearTimeout(timer);
    };
  }, [shouldShow]);

  if (!shouldShow) return null;

  const navItems = [
    { id: "slots", label: "Slots", href: "/games/slots" },
    { id: "table", label: "Table", href: "/games/table" },
    { id: "live", label: "Live", href: "/games/live" },
    { id: "instant", label: "Instant", href: "/games/instant" },
    { id: "favorites", label: "Favorites", href: "/games/favorites" },
  ];

  return (
    <nav
      className={`
        fixed left-0 right-0 z-40
        bg-card border-t border-border
        md:hidden
        transition-transform duration-300 ease-out
        ${isVisible ? "translate-y-0" : "translate-y-full"}
      `}
      style={{ bottom: `${bottomOffset}px` }}
    >
      <div className="flex justify-around items-center py-1 px-2">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => navigate(item.href)}
            className={`
              relative flex-1 text-center py-1 px-1
              text-xs font-medium transition-colors duration-150
              active:scale-95 min-h-8
              ${activeId === item.id ? "text-primary" : "text-gray-300 hover:text-white"}
            `}
          >
            {item.label}
          </button>
        ))}
      </div>
    </nav>
  );
};

export default GamesPageMobileNavigation;
